import hotToast from 'react-hot-toast'
import {
  showToastError,
  showToastInfo,
  showToastSuccess,
  showToastWarning,
  type NotificationOptions,
} from './notifications'

type ToastOptions = {
  autoClose?: number | boolean
  toastId?: string
}

function toNotificationOptions(
  options?: ToastOptions,
): NotificationOptions | undefined {
  if (!options) return undefined
  const result: NotificationOptions = {}
  if (options.toastId) result.id = options.toastId
  if (options.autoClose === false) result.duration = Infinity
  else if (typeof options.autoClose === 'number')
    result.duration = options.autoClose
  return result.id != null || result.duration != null ? result : undefined
}

export const toast = {
  success: (message: string, options?: ToastOptions) => {
    return showToastSuccess(message, toNotificationOptions(options))
  },

  error: (message: string, options?: ToastOptions) => {
    return showToastError(message, toNotificationOptions(options))
  },

  warning: (message: string, options?: ToastOptions) => {
    return showToastWarning(message, toNotificationOptions(options))
  },

  info: (message: string, options?: ToastOptions) => {
    return showToastInfo(message, toNotificationOptions(options))
  },

  update: (toastId: string, options: { render: string }) => {
    showToastInfo(options.render, { id: toastId, duration: Infinity })
  },

  dismiss: (toastId?: string) => {
    if (toastId) {
      hotToast.dismiss(toastId)
    } else {
      hotToast.dismiss()
    }
  },

  show: (
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    options?: ToastOptions,
  ) => {
    switch (type) {
      case 'success':
        return showToastSuccess(message, toNotificationOptions(options))
      case 'error':
        return showToastError(message, toNotificationOptions(options))
      case 'warning':
        return showToastWarning(message, toNotificationOptions(options))
      case 'info':
      default:
        return showToastInfo(message, toNotificationOptions(options))
    }
  },

  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: any) => string)
    },
  ) => {
    showToastInfo(messages.loading)

    return promise
      .then(data => {
        const successMessage =
          typeof messages.success === 'function'
            ? messages.success(data)
            : messages.success
        showToastSuccess(successMessage)
        return data
      })
      .catch(error => {
        const errorMessage =
          typeof messages.error === 'function'
            ? messages.error(error)
            : messages.error
        showToastError(errorMessage)
        throw error
      })
  },
}

export default toast
